/**
 * Phenomenon definitions for the psychedelic visualizer.
 *
 * Each phenomenon is grounded in a specific result from altered-state
 * phenomenology and mapped to a dominant 5D-ASC dimension (Dittrich 1998).
 * The active phenomenon reconfigures the scene's shader layers.
 */

export type PhenomenonId =
  | 'tunnel'
  | 'lattice'
  | 'spiral'
  | 'wavyform'
  | 'oceanic';

export interface Phenomenon {
  id: PhenomenonId;
  name: string;
  sub: string;
  /** Klüver form constant family this phenomenon expresses. */
  formConstant: string;
  /** Primary 5D-ASC dimension it embodies. */
  ascDimension: string;
  /** Phenomenological note shown in the UI. */
  note: string;
}

export const PHENOMENA: Phenomenon[] = [
  {
    id: 'tunnel',
    name: 'Tunnel',
    sub: 'Hyperbolic Portal',
    formConstant: 'Tunnel / Funnel',
    ascDimension: 'Oceanic Boundlessness',
    note: 'DMT-breakthrough portal. V1 radial eigenmode under negative curvature (Atasoy, Lehar).',
  },
  {
    id: 'lattice',
    name: 'Lattice',
    sub: 'Cortical Grid',
    formConstant: 'Lattice / Honeycomb',
    ascDimension: 'Visionary Restructuralization',
    note: 'V1 2D lattice eigenmode rendered as a deforming mesh landscape (Bressloff).',
  },
  {
    id: 'spiral',
    name: 'Spiral',
    sub: 'Log-Polar Arm',
    formConstant: 'Spiral',
    ascDimension: 'Audiovisual Synesthesia',
    note: 'Diagonal cortical plane wave → logarithmic spiral arms under the inverse map.',
  },
  {
    id: 'wavyform',
    name: 'Wavy Form',
    sub: 'SDF Entity',
    formConstant: 'Cobweb (branching)',
    ascDimension: 'Visionary Restructuralization',
    note: 'Raymarched smooth-min SDF morph — sphere↔torus↔cube. Infinite-LOD "entity".',
  },
  {
    id: 'oceanic',
    name: 'Oceanic',
    sub: 'Boundless Field',
    formConstant: 'Tunnel (dissolved)',
    ascDimension: 'Oceanic Boundlessness / Ego Dissolution',
    note: 'Particle field dissolving past the depth plane — loss of self/other boundary.',
  },
];

export interface Palette {
  id: string;
  name: string;
  /** three anchor colors (HDR-ready) used by shaders as vec3 uniforms. */
  a: [number, number, number];
  b: [number, number, number];
  c: [number, number, number];
  /** 'warm' = psychedelic (psilocybin/LSD), 'cold' = dissociative (ketamine). */
  family: 'warm' | 'cold';
}

// Palettes tuned to reported color phenomenology: saturated full-spectrum
// for serotonergic psychedelics, desaturated cold blues for dissociatives.
export const PALETTES: Palette[] = [
  { id: 'dmt', name: 'DMT', family: 'warm',
    a: [1.0, 0.2, 0.9], b: [0.2, 0.9, 1.0], c: [1.0, 0.9, 0.2] },
  { id: 'lsd', name: 'LSD', family: 'warm',
    a: [1.0, 0.4, 0.3], b: [0.3, 1.0, 0.5], c: [0.5, 0.3, 1.0] },
  { id: 'psilo', name: 'Psilocybin', family: 'warm',
    a: [0.9, 0.6, 0.2], b: [0.3, 0.7, 0.9], c: [0.7, 0.2, 0.8] },
  { id: 'keta', name: 'Ketamine', family: 'cold',
    a: [0.3, 0.5, 1.0], b: [0.5, 0.7, 1.0], c: [0.7, 0.8, 1.0] },
  { id: 'mxpr', name: 'Dissociative', family: 'cold',
    a: [0.4, 0.4, 0.8], b: [0.2, 0.6, 0.9], c: [0.6, 0.5, 0.95] },
];

/** A plain-data uniform set shared across the scene (drive from controls). */
export interface PsyState {
  phenomenon: PhenomenonId;
  paletteIndex: number;
  /** 0..1 — overall intensity; scales entropy (REBUS) & symmetry. */
  dose: number;
  /** 0..1 — how strongly audio modulates the scene. */
  reactivity: number;
  /** toggle: full post-processing stack on/off. */
  postFx: boolean;
}
